/**
 * Chakra Dimensions Configuration
 *
 * Central configuration for all chakra rendering dimensions:
 * - Sector width (majorDivisionLength)
 * - Label offsets from center line
 * - Used by all chakra types: Muhurta, Nakshatra, D7-D60, etc.
 */

export interface ChakraDimensionsConfig {
    /**
     * Width of chakra sector band (from center line)
     * Used in path generation for sector rendering
     */
    sectorWidth: number;

    /**
     * Label positions relative to sector edge
     */
    labels: {
        /**
         * Inner label offset (sector number)
         * Positive = outward from center, Negative = inward
         * Formula: sectorWidth - innerLabelInset
         */
        innerLabelInset: number;

        /**
         * Outer label offset (ruler code, nakshatra name, etc.)
         * Added to sectorWidth
         * Formula: sectorWidth + outerLabelOffset
         */
        outerLabelOffset: number;
    };
}

/**
 * Default chakra dimensions (same as D8, Muhurta, etc.)
 */
export const DEFAULT_CHAKRA_DIMENSIONS: ChakraDimensionsConfig = {
    sectorWidth: 100,  // Wide sector band (visible width)
    labels: {
        innerLabelInset: 40,   // 200 - 40 = 160px from center (inside sector, near outer edge)
        outerLabelOffset: 25   // 200 + 25 = 225px from center (outside sector)
    }
};

/**
 * Get chakra dimensions configuration
 * Can be extended to support per-chakra-type dimensions in future
 */
export function getChakraDimensions(chakraType?: string): ChakraDimensionsConfig {
    // Currently all chakra types use same dimensions
    // Can add chakra-specific overrides here if needed
    return DEFAULT_CHAKRA_DIMENSIONS;
}

/**
 * Calculate label offset from center line
 */
export function calculateInnerLabelOffset(config: ChakraDimensionsConfig = DEFAULT_CHAKRA_DIMENSIONS): number {
    return config.sectorWidth - config.labels.innerLabelInset;
}

/**
 * Calculate outer label offset from center line
 */
export function calculateOuterLabelOffset(config: ChakraDimensionsConfig = DEFAULT_CHAKRA_DIMENSIONS): number {
    return config.sectorWidth + config.labels.outerLabelOffset;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rashi Chakra (South Indian square grid) layout constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rashi Chakra SVG layout constants
 * Used by RashiChakraRenderer for viewBox and chart spacing
 */
export const RASHI_CHAKRA_LAYOUT = {
    /** SVG viewBox size (width = height) */
    VIEWBOX_SIZE: 700,

    /** Padding around the chart area */
    CHART_PADDING: 10,

    /** Gap between side-by-side charts (e.g., D1 + D9) */
    CHART_GAP: 20,

    /** Gap between outer and inner grid borders */
    OUTER_BORDER_GAP: 2,
} as const;
